import * as _ from 'lodash';
import { TransactionErrors } from '../objects/WageGroup';
import { getAttendanceOrdering, timeReportCommand, timeReportState } from '../objects/Attendance';
import { wageModelGroups } from '../objects/WageType';
import { compareGroup } from './PaymentGroupModel';
import { ProposedGroupModel } from './proposed/ProposedGroupModel';
import { StandardCalculationContext } from './calculation/StandardCalculator';
import { certificationStates, remunerationTypes } from '../objects/Payslip';
import { ComplexGroupModel } from './complex/ComplexGroupModel';
import { SimpleGroupModel } from './simple/SimpleGroupModel';
import { ProposedTimeGroupModel } from './proposedTime/ProposedTimeGroupModel';
import { MeetingCertifierSource } from '../objects/Meeting';

/**
 * @typedef { import('../objects/Payment').Payment } Payment
 * @typedef { import('../objects/Claim').Claim } Claim
 * @typedef { import('../objects/Payslip').Remuneration } Remuneration
 * @typedef { import('../objects/Attendance').Attendance } Attendance
 * @typedef { import('../objects/Payslip').Payslip } Payslip
 * @typedef { import('../objects/WageCalculation').WageCalculation } WageCalculation
 * @typedef { import('./PayslipCollection').PayslipCollection } PayslipCollection
 * @typedef { import('./PaymentGroupModel').PaymentGroupModel } PaymentGroupModel
 * @typedef { import('./calculation/CalculatedPayment').CalculatedPayment } CalculatedPayment
 * @typedef { import('./calculation/StandardCalculator').StandardCalculator } StandardCalculator
 * @typedef { import('./calculation/PolitikerCalculator').PolitikerCalculator } PolitikerCalculator
*/

const attendanceOrdering = getAttendanceOrdering(x => x.attendance);

/**
 * @param {PayslipModel} payslipModel 
 * @param {Remuneration} remuneration 
 */
function createExistingModel(payslipModel, remuneration) {
    if (remuneration.CertificationState === certificationStates.recalled || remuneration.CertificationState === certificationStates.rejected) {
        return ComplexGroupModel.create(payslipModel, remuneration);
    } else {
        return SimpleGroupModel.createExisting(payslipModel, remuneration);
    }
}

/**
 * @param {PaymentGroupModel} pg
 */
function isPolitikerResponsePending(pg) {
    if (pg instanceof ProposedGroupModel) {
        return !pg.hasResponded;
    } else if (pg instanceof ProposedTimeGroupModel) {
        return !pg.hasResponded;
    } else {
        return false;
    }
}

export class PayslipModel {

    /**
     * @param {PayslipCollection} collection
     * @param {Payslip} payslip
     * @param {PolitikerCalculator} politikerCalculator
     * @param {StandardCalculator} meetingCalculator
     */
    constructor(collection, payslip, politikerCalculator, meetingCalculator) {
        this.collection = collection;
        this.politikerCalculator = politikerCalculator;

        this.needsSaving = false;
        this.hasPayments = false;

        this.attendance = payslip.Attendance;
        this.warnings = payslip.Warnings;
        this.enableLostIncome = !!payslip.EnableLostIncome;
        this.hasWarnings = payslip.Warnings && payslip.Warnings.length > 0;

        this.presentTime = this.attendance.PresentTime;
        this.servingTime = this.attendance.ServingTime;
        
        this.isWaitingTimeReport = false;

        this.paymentGroups = [];
        /** @type {ProposedTimeGroupModel} */
        this.timeReportGroup = null;

        if (this.attendance.TimeReport) {
            const tr = this.attendance.TimeReport;
            
            this.hasTimeReportInfo = tr.State !== timeReportState.recalled;

            if (tr.State === timeReportState.reported) {
                this.timeReportGroup = ProposedTimeGroupModel.create(this, meetingCalculator);
                this.paymentGroups.push(this.timeReportGroup);
            } else if (tr.State === timeReportState.requested || tr.State === timeReportState.rejected) {
                this.isWaitingTimeReport = true;
            }
        }

        for (let nc of payslip.NewClaims) {
            this.paymentGroups.push(ProposedGroupModel.create(this, nc, politikerCalculator));
        }

        for (let rem of payslip.Remunerations) {
            this.paymentGroups.push(createExistingModel(this, rem));
        }

        this.paymentGroups.sort(compareGroup);

        this._validate();
        this._updateDerived();
    }

    _validate() {
        this.transactionIssues = [];

        if (this.attendance.isLegacy()) {
            this.transactionIssues.push(TransactionErrors.isLegacy);
        } else {
            if (!this.attendance.Mission.EmployeeNumber) {
                this.transactionIssues.push(TransactionErrors.noEmployeeNumber);
            }

            const p = this.attendance.Mission.Person;
            if (!p) {
                this.transactionIssues.push(TransactionErrors.isVacant);
            } else if (p.PersonalNumber == null || p.PersonalNumber.length != 13) {
                this.transactionIssues.push(TransactionErrors.noPersonalNumber);
            }
        }

        this.transactionIssuesString = this.transactionIssues.map(x => x.short).join(', ');
        this.hasTransactionIssues = this.transactionIssues.length > 0;
    }

    _getIsMissingCertifier() {
        if (this.attendance.isLegacy()) {
            return false;
        }

        const meeting = this.attendance.Meeting;
        const mission = this.attendance.Mission;

        const certifyingOrganization = (meeting.CertifierSource === MeetingCertifierSource.meetingOrganization) ? meeting.Organization : mission.MandatePeriod.Organization;
        if (certifyingOrganization == null) {
            return true;
        }

        if (certifyingOrganization.PrimaryCertifierId == null) {
            return true;
        }

        if (certifyingOrganization.PrimaryCertifierId === mission.PersonId) {
            return certifyingOrganization.SecondaryCertifier == null;
        }

        return false;
    }

    _updateDerived() {
        this.hasPayments = false;
        this.needsSaving = false;
        this.isMissingMeetingPayment = (this.attendance.isLegacy() || this.attendance.Mission.HasMeetingPay) && (this.presentTime != null && this.presentTime.length > 0);
        this.hasPolitikerTasks = false;
        this.hasRejectedCertification = false;
        this.hasRecalledCertification = false;
        this.totalAmount = 0;

        let hasPendingCertification = false;

        for (let pg of this.paymentGroups) {
            this.needsSaving = this.needsSaving || pg.checkChanges();
            this.hasPayments = this.hasPayments || pg.payments.length > 0;
            this.isMissingMeetingPayment = this.isMissingMeetingPayment && !pg.payments.some(x => x.payment.WageType.WageModelGroup === wageModelGroups.meeting);
            
            this.hasRejectedCertification = this.hasRejectedCertification || pg.certRejected;
            this.hasRecalledCertification = this.hasRecalledCertification || pg.certRecalled;
            hasPendingCertification = hasPendingCertification || pg.certPending;
            
            this.hasPolitikerTasks = this.hasPolitikerTasks || isPolitikerResponsePending(pg);

            this.totalAmount = this.totalAmount + pg.getTotalAmount();
        }

        this.isMissingCertifier = hasPendingCertification && this._getIsMissingCertifier();
    }

    _paymentsChanged() {
        this._updateDerived();
        this.collection.payslipChanged(this);
    }

    /**
     * @param {PaymentGroupModel} _pg
     */
    paymentGroupChanged(_pg) {
        this._paymentsChanged();
    }

    /**
     * @param {ProposedTimeGroupModel} pg 
     * @param {boolean} accept 
     */
    proposedTimeChanged(pg, accept) {
        if (accept) {
            this.presentTime = pg.presentTime;
            this.servingTime = pg.servingTime;
            this._paymentsChanged();
        } else {
            this.presentTime = this.attendance.PresentTime;
            this.servingTime = this.attendance.ServingTime;
            this._paymentsChanged();
        }
    }

    /**
     * @param {PaymentGroupModel} pg
     * @param {Claim} newClaim
     */
    claimRecalled(pg, newClaim) {
        let index = this.paymentGroups.indexOf(pg);
        if (index >= 0) {
            this.paymentGroups = this.paymentGroups.slice();
            this.paymentGroups[index] = ProposedGroupModel.create(this, newClaim, this.politikerCalculator);
            this.paymentGroups.sort(compareGroup);
            this._paymentsChanged();
        }
    }

    /**
     * @param {PaymentGroupModel} pg
     * @param {Remuneration} newrem
     */
    certificationRecalled(pg, newrem) {
        let index = this.paymentGroups.indexOf(pg);
        if (index >= 0) {
            this.paymentGroups = this.paymentGroups.slice();
            this.paymentGroups[index] = createExistingModel(this, newrem);
            this.paymentGroups.sort(compareGroup);
            this._paymentsChanged();
        }
    }

    /**
     * 
     * @param {PaymentGroupModel} pg 
     */
    remunerationDeleted(pg) {
        let index = this.paymentGroups.indexOf(pg);
        if (index >= 0) {
            this.paymentGroups = this.paymentGroups.slice();
            this.paymentGroups.splice(index, 1);
            this._paymentsChanged();
        }
    }

    /**
     * Calculates payments using calculator.
     * @param {StandardCalculator} calculator
     */
    recalculate(calculator) {
        let newGroups = _.reject(this.paymentGroups, x => x.isManuallyCalculated);
        let hasRemoved = newGroups.length !== this.paymentGroups.length;
        let hasCalculated = false;

        if (calculator != null) {
            let ctx = new StandardCalculationContext(this.attendance);
            ctx.skipMeeting = _.some(newGroups, pg => _.some(pg.payments, x => x.payment.WageType.WageModelGroup === wageModelGroups.meeting));
            ctx.skipTravel = _.some(newGroups, pg => _.some(pg.payments, x => x.payment.WageType.WageModelGroup === wageModelGroups.travel));
            ctx.presentTime = this.presentTime;
            ctx.servingTime = this.servingTime;

            let meetingPayments = calculator.calculateMeeting(ctx);
            if (meetingPayments.length > 0) {
                newGroups.push(SimpleGroupModel.createCalculated(this, remunerationTypes.calculatedMeeting, calculator.rules.WageCalculation, meetingPayments));
                hasCalculated = true;
            }

            let travelPayments = calculator.calculateTravel(ctx);
            if (travelPayments.length > 0) {
                newGroups.push(SimpleGroupModel.createCalculated(this, remunerationTypes.calculatedTravel, calculator.rules.WageCalculation, travelPayments));
                hasCalculated = true;
            }

            if (hasCalculated) {
                newGroups.sort(compareGroup);
            }
        }

        if (hasRemoved || hasCalculated) {
            this.paymentGroups = newGroups;
            this._paymentsChanged();
        }
    }

    approveAllPolitiker() {
        if (!this.hasPolitikerTasks) return;

        for (let pg of this.paymentGroups) {
            if (isPolitikerResponsePending(pg)) {
                pg.accept();
            }
        }
    }

    /**
     * 
     * @param {Payment} payment
     */
    addPayment(payment) {
        let group = SimpleGroupModel.createManual(this, remunerationTypes.payment, [payment]);
        this.paymentGroups = _.concat(this.paymentGroups, group);
        this._paymentsChanged();
        return group.payments[0];
    }

    /**
     * 
     * @param {PaymentModel} existing
     * @param {Payment} updated
     */
    updatePayment(existing, updated) {
        existing.group.updatePayment(existing, updated);
        this._paymentsChanged();
    }

    /**
     * 
     * @param {PaymentModel} paymentModel
     */
    removePayment(paymentModel) {
        if (paymentModel.group.removePayment(paymentModel)) {
            this._paymentsChanged();
        }
    }

    /**
     * 
     * @param {(pm: PaymentModel)=>boolean} predicate
     */
    removeMatchingPayments(predicate) {
        let affected = false;

        for (let group of this.paymentGroups) {
            if (group.removeMatchingPayments(predicate)) {
                affected = true;
            }
        }

        if (affected) {
            this._paymentsChanged();
        }
    }

    createUpdate() {
        var ret = {
            AttendanceId: this.attendance.Id,
            Attendance: null,
            RowVersion: this.attendance.RowVersion,
            AddRemunerations: [],
            UpdateRemunerations: [],
            ClaimRejections: [],
        };

        if (this.timeReportGroup != null && this.timeReportGroup.hasResponded) {
            ret.Attendance = {
                PresentTime: this.presentTime,
                ServingTime: this.servingTime,
                Comment: this.attendance.Comment,
            };

            if (this.timeReportGroup.hasAccepted) {
                ret.Attendance.TimeReportOperation = timeReportCommand.accept;
            } else if (this.timeReportGroup.hasRejected) {
                ret.Attendance.TimeReportOperation = timeReportCommand.reject;
                ret.Attendance.RejectTimeReportMessage = this.timeReportGroup.rejectionMessage;
            }
        }

        for (let pg of this.paymentGroups) {
            if ((pg instanceof ProposedGroupModel) && pg.hasRejected) {
                ret.ClaimRejections.push({
                    Id: pg.claim.Id,
                    RejectionMessage: pg.rejectionMessage,
                });
            } else if (pg.isAdded) {
                let add = pg.createAdd();
                if (_.isArray(add)) {
                    ret.AddRemunerations.push(...add);
                } else if (add != null) {
                    ret.AddRemunerations.push(add);
                }
            } else if (pg.checkChanges()) {
                let update = pg.createUpdate();
                if (update != null) {
                    ret.UpdateRemunerations.push(update);
                }
            }
        }

        return ret;
    }

    /**
     * 
     * @param {PayslipModel[]} collection
     * @returns {PayslipModel[]}
     */
    static order(collection) {
        return _.orderBy(collection, attendanceOrdering);
    }
}
